import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import {
  BeakerIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline'
import { GET_RAW_MATERIALS } from '../../graphql/costing-queries'
import { RawMaterial } from '../../types/costing'
import { formatCostPerUnit } from '../../lib/costingUtils'
import LoadingSpinner from '../../components/LoadingSpinner'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'

const RecipesPage = () => {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const canManage = hasPermission(PERMISSIONS.COSTING_MANAGE)

  const [search, setSearch] = useState('')

  const { data, loading, error } = useQuery(GET_RAW_MATERIALS, {
    variables: {
      filters: {
        type: 'recipe',
        search: search || undefined,
      },
    },
  })

  const recipes: RawMaterial[] = (data?.rawMaterials ?? []).filter(
    (m: RawMaterial) => m.type === 'recipe'
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Recipes</h1>
          <p className="mt-1 text-sm text-gray-500">
            Raw materials whose cost is derived from a list of ingredients.
          </p>
        </div>
        {canManage && (
          <Link
            to="/costing/recipes/new"
            className="inline-flex items-center gap-x-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
          >
            <PlusIcon className="h-5 w-5" />
            New Recipe
          </Link>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search recipes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-9 w-full"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">{error.message}</div>
      ) : recipes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
          <BeakerIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-sm font-semibold text-gray-900">No recipes found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {search ? 'Try a different search term.' : 'Create your first recipe to get started.'}
          </p>
          {canManage && !search && (
            <Link
              to="/costing/recipes/new"
              className="mt-6 inline-flex items-center gap-x-2 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
            >
              <PlusIcon className="h-5 w-5" />
              New Recipe
            </Link>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Output Qty
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost per unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inventory tracking
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recipes.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/costing/recipes/${r.id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <BeakerIcon className="h-4 w-4 text-primary-500 shrink-0" />
                      <span className="text-sm font-medium text-gray-900">{r.name}</span>
                    </div>
                    {r.notes && (
                      <p className="mt-0.5 text-xs text-gray-500 truncate max-w-xs">{r.notes}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {r.unit}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {r.output_quantity != null ? `${r.output_quantity} ${r.unit}` : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {formatCostPerUnit(r.cost_per_unit_millicents, r.unit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      r.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {r.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {r.track_inventory ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-600 text-white">
                        <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                        Tracked
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-gray-400 border border-dashed border-gray-200">
                        <ClipboardDocumentListIcon className="h-3.5 w-3.5" />
                        Not tracked
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default RecipesPage
